import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { TopBar } from "../components/layout/TopBar";
import { Spinner } from "../components/shared/Spinner";
import { Button } from "../components/shared/Button";
import { ConflictView } from "../components/compare/ConflictView";
import { buildConflictsFromText, conflictCount } from "../lib/conflict-merge";
import { api, type GitDocEntry } from "../api/client";
import { parseFrontMatter } from "../../shared/front-matter";
import { useGitStore } from "../stores/useGitStore";
import { useToastStore } from "../stores/useToastStore";
import { formatApiError } from "../lib/api-errors";
import { versionSortKey } from "../../shared/versions";

export interface SuggestionsLocationState {
  draftBody?: string;
  draftVersion?: string;
  draftTitle?: string;
  editPath?: string;
}

export function SuggestionsPage() {
  const { slug: slugParam } = useParams<{ slug: string }>();
  const slug = slugParam ? decodeURIComponent(slugParam) : "";
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const fetchTree = useGitStore((s) => s.fetchTree);
  const addToast = useToastStore((s) => s.addToast);

  const navState = (location.state ?? {}) as SuggestionsLocationState;

  const [docs, setDocs] = useState<GitDocEntry[]>([]);
  const [history, setHistory] = useState<
    Array<{ sha: string; date: string; message: string }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState<string | null>(null);

  const [fromPath, setFromPath] = useState(searchParams.get("from") ?? "");
  const [toPath, setToPath] = useState(searchParams.get("to") ?? "");
  const [fromRef, setFromRef] = useState(searchParams.get("fromRef") ?? "");
  const [toRef, setToRef] = useState(searchParams.get("toRef") ?? "");

  const [oldBody, setOldBody] = useState("");
  const [newBody, setNewBody] = useState(navState.draftBody ?? "");
  const [usingDraft, setUsingDraft] = useState(!!navState.draftBody);

  const familyTitle = docs[0]?.title ?? navState.draftTitle ?? "Document";

  useEffect(() => {
    if (navState.draftBody) {
      setNewBody(navState.draftBody);
      setUsingDraft(true);
    }
  }, [navState.draftBody]);

  useEffect(() => {
    setLoading(true);
    fetchTree()
      .then((all) => {
        const familyDocs = all
          .filter((d) => d.family === slug)
          .sort(
            (a, b) => versionSortKey(a.version) - versionSortKey(b.version),
          );
        setDocs(familyDocs);

        setFromPath(
          (prev) => prev || familyDocs[familyDocs.length - 2]?.path || "",
        );
        setToPath(
          (prev) => prev || familyDocs[familyDocs.length - 1]?.path || "",
        );
      })
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [slug, fetchTree]);

  useEffect(() => {
    if (!slug) return;
    api.git
      .log(slug)
      .then(setHistory)
      .catch(() => setHistory([]));
  }, [slug]);

  // Sync query string when selectors change.
  useEffect(() => {
    if (!fromPath || !toPath) return;
    const next = new URLSearchParams();
    next.set("from", fromPath);
    next.set("to", toPath);
    if (fromRef) next.set("fromRef", fromRef);
    if (toRef) next.set("toRef", toRef);
    setSearchParams(next, { replace: true });
  }, [fromPath, toPath, fromRef, toRef, setSearchParams]);

  useEffect(() => {
    if (!fromPath || fromPath === toPath) return;
    let cancelled = false;
    setDiffLoading(true);
    setDiffError(null);
    api.git
      .file(fromPath, fromRef || undefined)
      .then((f) => {
        if (!cancelled) setOldBody(parseFrontMatter(f.content).content);
      })
      .catch((e) => {
        if (!cancelled)
          setDiffError(
            e instanceof Error ? e.message : "Failed to load baseline",
          );
      })
      .finally(() => {
        if (!cancelled) setDiffLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fromPath, fromRef, toPath]);

  useEffect(() => {
    if (usingDraft || !toPath || fromPath === toPath) return;
    let cancelled = false;
    setDiffLoading(true);
    api.git
      .file(toPath, toRef || undefined)
      .then((f) => {
        if (!cancelled) setNewBody(parseFrontMatter(f.content).content);
      })
      .catch((e) => {
        if (!cancelled)
          setDiffError(
            e instanceof Error ? e.message : "Failed to load version",
          );
      })
      .finally(() => {
        if (!cancelled) setDiffLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [toPath, toRef, usingDraft, fromPath]);

  const conflictSegments = useMemo(() => {
    if (!oldBody || newBody === "") return null;
    return buildConflictsFromText(oldBody, newBody);
  }, [oldBody, newBody]);

  const fromLabel = docs.find((d) => d.path === fromPath)?.version ?? fromPath;
  const toLabel =
    usingDraft && navState.draftVersion
      ? `${navState.draftVersion} (draft)`
      : (docs.find((d) => d.path === toPath)?.version ?? toPath);

  const applyMerged = useCallback(
    (merged: string) => {
      setNewBody(merged);
      setUsingDraft(true);
      addToast("merge applied to draft", "success");
    },
    [addToast],
  );

  const copyText = useCallback(
    (text: string, label: string) => {
      navigator.clipboard?.writeText(text).then(
        () => addToast(label, "success"),
        () => addToast("copy failed", "error"),
      );
    },
    [addToast],
  );

  const backToEditor = () => {
    if (!navState.editPath) {
      navigate(`/family/${encodeURIComponent(slug)}`);
      return;
    }
    // Always carry the in-progress local copy back to the editor so it never
    // reloads a stale git version (which would otherwise force a "restore local").
    const localBody = usingDraft ? newBody : navState.draftBody;
    navigate(`/doc/${encodeURIComponent(navState.editPath)}`, {
      state:
        localBody !== undefined ? { restoredMarkdown: localBody } : undefined,
    });
  };

  if (loading) {
    return (
      <>
        <TopBar title="loading suggestions..." />
        <div style={styles.center}>
          <Spinner size={24} label="loading…" />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <TopBar title="error" />
        <div style={styles.center}>
          <p style={styles.error}>{error}</p>
          <Button type="button" onClick={() => navigate("/")}>
            dashboard
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <TopBar
        title={familyTitle}
        actions={
          <div style={styles.actions}>
            {navState.editPath && (
              <Button type="button" variant="primary" onClick={backToEditor}>
                continue in editor
              </Button>
            )}
            <Button type="button" onClick={backToEditor}>
              back
            </Button>
            <Button type="button" onClick={() => navigate("/")}>
              dashboard
            </Button>
          </div>
        }
      />

      <div style={styles.page}>
        <div style={styles.toolbar}>
          <select
            value={fromPath}
            onChange={(e) => setFromPath(e.target.value)}
            style={styles.select}
            aria-label="From version"
          >
            {docs.map((d) => (
              <option key={d.path} value={d.path}>
                {d.version}
              </option>
            ))}
          </select>
          <select
            value={fromRef}
            onChange={(e) => setFromRef(e.target.value)}
            style={styles.select}
            aria-label="From git ref"
          >
            <option value="">current</option>
            {history.map((h) => (
              <option key={h.sha} value={h.sha}>
                {h.sha.slice(0, 7)}
              </option>
            ))}
          </select>
          <span style={styles.arrow}>→</span>
          <select
            value={toPath}
            onChange={(e) => {
              setToPath(e.target.value);
              setUsingDraft(false);
            }}
            style={styles.select}
            aria-label="To version"
          >
            {docs.map((d) => (
              <option key={d.path} value={d.path}>
                {d.version}
              </option>
            ))}
          </select>
          <select
            value={toRef}
            onChange={(e) => setToRef(e.target.value)}
            style={styles.select}
            aria-label="To git ref"
            disabled={usingDraft}
          >
            <option value="">current</option>
            {history.map((h) => (
              <option key={h.sha} value={h.sha}>
                {h.sha.slice(0, 7)}
              </option>
            ))}
          </select>
          {usingDraft && <span style={styles.draftBadge}>unsaved draft</span>}
        </div>

        {diffLoading && (
          <div style={styles.center}>
            <Spinner size={20} label="loading…" />
          </div>
        )}
        {diffError && <p style={styles.error}>{diffError}</p>}
        {!diffLoading &&
          !diffError &&
          conflictSegments &&
          (conflictCount(conflictSegments) > 0 ? (
            <ConflictView
              segments={conflictSegments}
              fromLabel={fromLabel}
              toLabel={toLabel}
              onApply={applyMerged}
              onCopy={copyText}
            />
          ) : (
            <p style={styles.empty}>No differences in document body.</p>
          ))}
      </div>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: "flex",
    flexDirection: "column",
    minHeight:
      "calc(100vh - var(--topnav-height) - var(--utilbar-height) - var(--pagehead-height))",
    padding: "0 var(--page-pad-x) var(--page-pad-b)",
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 10,
    padding: "16px 0",
    borderBottom: "1px solid var(--comp-rule)",
    marginBottom: 16,
  },
  select: {
    padding: "8px 12px",
    border: "1px solid var(--comp-rule)",
    borderRadius: "var(--radius-md)",
    fontSize: 13,
    fontFamily: "var(--font-mono)",
    background: "var(--comp-paper)",
    color: "var(--fg-1)",
  },
  arrow: {
    color: "var(--fg-3)",
    fontSize: 14,
  },
  draftBadge: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-3)",
    padding: "2px 0",
  },
  center: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 48,
    gap: 12,
  },
  error: {
    color: "var(--comp-salmon)",
    fontSize: 14,
    padding: 16,
  },
  empty: {
    padding: 32,
    color: "var(--fg-3)",
    fontSize: 14,
  },
  actions: {
    display: "flex",
    gap: 8,
  },
};
